import { Injectable, inject, signal } from '@angular/core';
import { Guid } from 'guid-typescript';
import {
  SiaTemplateNotificationOptions,
  SiaTemplateNotificationRequest,
} from '../interfaces/sia-template-notification.interface';
import { SiaComponentConfigService } from './component-config.service';

/**
 * Orchestratore singleton per le notifiche-template a discesa dall'alto.
 *
 * - Coda FIFO: apre una sola notifica alla volta; le successive restano in buffer.
 * - La Promise ritornata da `show(...)` risolve alla chiusura di QUESTA notifica
 *   (non al momento dell'accodamento), cosi gli script possono usare `await`.
 * - Il timer di auto-dismiss si avvia in entrambe le modalita' (normale e
 *   fullScreen) quando `durationMs > 0`. `durationMs === 0` -> sticky
 *   (nessun timer, chiusura solo manuale via X o ESC).
 * - Hover sul pannello -> `pauseTimer()` / mouseleave -> `resumeTimer()`:
 *   comportamento di pausa (riparte dal tempo rimanente, non da zero). Valido
 *   in entrambe le modalita'.
 */
@Injectable({ providedIn: 'root' })
export class SiaTemplateNotificationService {
  static readonly CONFIG_TYPE = 'template-notification';
  static readonly DEFAULT_DURATION_MS = 5000;
  static readonly ANIMATION_OUT_MS = 300;

  readonly current = signal<SiaTemplateNotificationRequest | null>(null);

  readonly #componentConfigService = inject(SiaComponentConfigService);
  readonly #queue: SiaTemplateNotificationRequest[] = [];

  #timerHandle: any = null;
  #timerStartedAt = 0;
  #timerRemainingMs = 0;

  /**
   * Apre (o accoda) una notifica-template.
   * Risolve quando QUESTA notifica viene chiusa.
   */
  async show(options: SiaTemplateNotificationOptions): Promise<void> {
    // Risoluzione prospettiva "di default" per (configType, componentId) con
    // merge core/customer/private — NON `getPerspective` che richiede il GUID
    // del record di prospettiva.
    const perspective = await this.#componentConfigService.getDefaultPerspective(
      SiaTemplateNotificationService.CONFIG_TYPE,
      options.componentId,
      'MASTER',
    );
    let template: any = null;
    try {
      const raw = perspective?.jsonConfig ?? perspective?.unprocessedJsonConfig ?? null;
      template = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      console.error('SiaTemplateNotificationService: impossibile parsare la configurazione template', e);
      return;
    }
    if (!template) {
      console.warn(
        `SiaTemplateNotificationService: configurazione template non trovata per componentId "${options.componentId}"`,
      );
      return;
    }

    return new Promise<void>((resolve) => {
      const req: SiaTemplateNotificationRequest = {
        guid: Guid.create().toString(),
        options,
        template,
        fullScreen: template?.fullScreen === true,
        resolve,
      };
      this.#enqueue(req);
    });
  }

  /**
   * Variante "inline" usata per la preview dal configuratore: accetta direttamente
   * un SiaTemplateConfiguration gia' in memoria (shape `any` per evitare dipendenza
   * circolare @sia/shared -> @sia/template), senza risolverlo da una prospettiva.
   * Riusa lo stesso flusso di coda/timer/close di `show()`.
   */
  showInline(template: any, options?: Partial<Omit<SiaTemplateNotificationOptions, 'componentId'>>): Promise<void> {
    if (!template) {
      console.warn('SiaTemplateNotificationService.showInline: template vuoto');
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const req: SiaTemplateNotificationRequest = {
        guid: Guid.create().toString(),
        options: {
          componentId: '',
          data: options?.data,
          durationMs: options?.durationMs,
          type: options?.type,
        },
        template,
        fullScreen: template?.fullScreen === true,
        resolve,
      };
      this.#enqueue(req);
    });
  }

  /**
   * Chiude la notifica attiva, risolve la sua Promise e apre la prossima in coda.
   */
  close(): void {
    const active = this.current();
    if (!active) return;
    this.#clearTimer();
    this.current.set(null);
    try {
      active.resolve();
    } catch {
      /* noop */
    }
    // piccolo delay per permettere l'animazione di uscita prima di processare la coda
    setTimeout(() => this.#processQueue(), SiaTemplateNotificationService.ANIMATION_OUT_MS);
  }

  /**
   * Accoda una richiesta e tenta di processare la coda. Centralizza l'ingresso
   * di nuove notifiche per evitare race condition tra `show()` concorrenti.
   */
  #enqueue(req: SiaTemplateNotificationRequest): void {
    this.#queue.push(req);
    this.#processQueue();
  }

  /**
   * Apre la prossima notifica in coda solo se non ce n'e' gia' una attiva.
   * Garantisce serializzazione: chiamate concorrenti non possono sovrascrivere
   * `current()` perche' lo shift e l'open avvengono in modo sincrono.
   */
  #processQueue(): void {
    if (this.current() !== null) return;
    const next = this.#queue.shift();
    if (next) this.#open(next);
  }

  /**
   * Sospende il timer (hover sul pannello). Conserva il tempo rimanente.
   */
  pauseTimer(): void {
    const active = this.current();
    if (!active || this.#timerHandle === null) return;
    const elapsed = Date.now() - this.#timerStartedAt;
    this.#timerRemainingMs = Math.max(0, this.#timerRemainingMs - elapsed);
    clearTimeout(this.#timerHandle);
    this.#timerHandle = null;
  }

  /**
   * Riprende il timer dal tempo rimanente (mouseleave).
   */
  resumeTimer(): void {
    const active = this.current();
    if (!active || this.#timerHandle !== null) return;
    if (this.#timerRemainingMs <= 0) {
      this.close();
      return;
    }
    this.#timerStartedAt = Date.now();
    this.#timerHandle = setTimeout(() => this.close(), this.#timerRemainingMs);
  }

  #open(req: SiaTemplateNotificationRequest): void {
    this.current.set(req);
    // Timer attivo per entrambe le modalita' (normale / fullScreen).
    // `durationMs === 0` -> sticky: nessun auto-dismiss, chiusura solo manuale.
    const duration = req.options.durationMs ?? SiaTemplateNotificationService.DEFAULT_DURATION_MS;
    if (duration <= 0) return;
    this.#timerRemainingMs = duration;
    this.#timerStartedAt = Date.now();
    this.#timerHandle = setTimeout(() => this.close(), duration);
  }

  #clearTimer(): void {
    if (this.#timerHandle !== null) {
      clearTimeout(this.#timerHandle);
      this.#timerHandle = null;
    }
    this.#timerRemainingMs = 0;
    this.#timerStartedAt = 0;
  }
}
